'use client'
import { type FC, memo, useMemo } from 'react'
import { useTranslator } from '@/hooks'
import { E_PRODUCT_NUTRITION_TYPE, type TypeCmsSpecification } from '@/schema'
import styled from 'styled-components'
import { mq, presets, rgbAlpha, vw } from '@/styles'

type Props = {
  data: TypeCmsSpecification[]
}

export const formatNutritionalValue = (value: unknown, locale: string = 'es-ES') => {
  const normalizedLocale = (locale === 'es-CT' || locale === 'es-MD') ? 'es-ES' : locale
  const cleanString = String(value)
    .replace(/[^\d.,-]/g, '') // remove any char that's not digit, dot, comma, minus
    .replace(',', '.')        // replace comma with dot for Number conversion

  const numericValue = Number(cleanString)
  if (!Number.isFinite(numericValue)) {
    return 'N/A' // or empty string, or whatever fits
  }

  if (numericValue < 1) {
    return '< 1'
  }

  return numericValue.toLocaleString(normalizedLocale)
}

const getNutritionalRows = (details: Props['data']) =>
  details.map(({ name, value }, idx) => {

    return (
      <tr key={idx}>
        <td>{name}</td>
        <td>{value}</td>
      </tr>
    )
  })

export const SectionNutritionChart: FC<Props> = memo(({ data }) => {
  const { t } = useTranslator()
  const energy = useMemo(() => data.find(item => item.nutritionType === E_PRODUCT_NUTRITION_TYPE.ENERGY), [data])
  const chartData = useMemo(() => data.filter(item => item.nutritionType === E_PRODUCT_NUTRITION_TYPE.WEIGHT),[data])
  const rows = useMemo(() => getNutritionalRows(chartData), [chartData])

  return (
    <SectionNutrition>
      <H2>{t('project.product.product_description.nutritional_value.title')}</H2>
      <P>
        <span>{t('project.product.product_description.nutritional_value.average_value_per')} 100gr.</span>
        {energy && (
          <span>
            {t('project.product.product_description.nutritional_value.energy')}: {energy.value}
          </span>
        )}
      </P>
      <Table>
        <tbody>{rows}</tbody>
      </Table>
    </SectionNutrition>
  )
})

const P = styled.p`
  align-items: center;
  display: flex;
  height: ${vw(25, 'mobile')};
  justify-content: center;

  ${mq.greaterThan('nexus7')} {
    height: ${vw(25, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(25, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    height: 25px;
  }

  span {
    ${presets.font12_1}
    align-items: center;
    color: var(--color-text);
    display: flex;
    height: 100%;
    justify-content: center;
    padding: 0 ${vw(25, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: 0 ${vw(25, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: 0 ${vw(25, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 0 25px;
    }

    &:nth-child(2) {
      border-left: 1px solid var(--color-text);
    }
  }
`

const SectionNutrition = styled.section`
  table td {
    &:nth-child(even) {
     text-align: right;
     padding-right: ${vw(25, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        padding-right: ${vw(25, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        padding-right: ${vw(25, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        padding-right: 25px;
      }
    }
  }
`

const H2 = styled.h2`
  ${presets.font53}
  color: var(--color-text--darker);
  margin: ${vw(60, 'mobile')} 0 ${vw(20, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    margin: ${vw(80, 'tablet')} 0 ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    margin: ${vw(80, 'desktop')} 0 ${vw(20, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin: 80px 0 20px;
  }
`

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;

  tr:not(:last-child) {
    border-bottom: 1px solid ${rgbAlpha('--color-text--darker', .2)};
  }

  td {
  	${presets.font15_1}
		color: var(--color-text);
    padding: ${vw(16.5, 'mobile')} 0;
    vertical-align: top;
    width: 50%;

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(16.5, 'tablet')} 0;
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(16.5, 'desktop')} 0;
    }

    ${mq.greaterThan('desktop')} {
      padding: 16px 0;
    }

    &:nth-child(odd) {
      color: var(--color-text--darker);
    }
  }
`
